import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getTenantReadinessSummary } from '../api/authApi';
import { FilterBar, FilterSelect, FilterSelectOption } from '../components/FilterBar';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { DataTable } from '../components/DataTable';
import { TableToolbar } from '../components/TableToolbar';
import { IconButton } from '../components/ui/IconButton';
import { formatReadinessStatus, formatTenantStatus } from '../utils/systemLabels';

const STATUS_KEYS = ['', 'not_ready', 'partially_ready', 'ready_for_manual_review', 'ready'];

function readinessTone(status) {
  if (status === 'ready') return 'Success';
  if (status === 'partially_ready') return 'Warning';
  if (status === 'ready_for_manual_review') return 'Info';
  if (status === 'not_ready') return 'Danger';
  return 'Neutral';
}

export function ProvisioningReadinessPage() {
  const { t } = useTranslation(['onboarding', 'common', 'navigation']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overallStatus, setOverallStatus] = useState(searchParams.get('overall_status') || '');

  const statusOptions = useMemo(
    () => STATUS_KEYS.map((value) => ({
      value,
      label: value ? t(`onboarding:readiness.statuses.${value}`) : t('onboarding:readiness.statuses.all'),
    })),
    [t],
  );

  useEffect(() => {
    setLoading(true);
    setError('');

    getTenantReadinessSummary({
      per_page: 100,
      overall_status: overallStatus || undefined,
    })
      .then((response) => setRows(response.data || []))
      .catch(() => setError(t('onboarding:readiness.load_failed')))
      .finally(() => setLoading(false));
  }, [overallStatus, t]);

  const emptyValue = t('common:empty_value');
  const recordCount = rows.length;

  const columns = useMemo(
    () => [
      {
        id: 'tenant',
        label: t('onboarding:readiness.columns.tenant'),
        render: (row) => row.tenant?.name || emptyValue,
      },
      {
        id: 'customer',
        label: t('onboarding:readiness.columns.customer'),
        render: (row) => row.customer?.name || emptyValue,
      },
      {
        id: 'tenant_status',
        label: t('onboarding:readiness.columns.tenant_status'),
        render: (row) => (
          row.status
            ? (
              <StatusBadge
                status={row.status}
                category="tenant"
                apiLabel={formatTenantStatus(row.status) || row.status}
                size="Sm"
              />
            )
            : emptyValue
        ),
      },
      {
        id: 'overall_readiness',
        label: t('onboarding:readiness.columns.overall_readiness'),
        render: (row) => (
          row.overall_status
            ? (
              <StatusBadge
                status={row.overall_status}
                category="generic"
                apiLabel={
                  formatReadinessStatus(row.overall_status)
                  || t(`onboarding:readiness.statuses.${row.overall_status}`, row.overall_status)
                }
                size="Sm"
                tone={readinessTone(row.overall_status)}
              />
            )
            : emptyValue
        ),
      },
      {
        id: 'score',
        label: t('onboarding:readiness.columns.score'),
        render: (row) => `${row.completed_count ?? emptyValue}/${row.total_count ?? emptyValue}`,
      },
      {
        id: 'blocking',
        label: t('onboarding:readiness.columns.blocking'),
        render: (row) => row.blocking_issues_count ?? emptyValue,
      },
      {
        id: 'warnings',
        label: t('onboarding:readiness.columns.warnings'),
        render: (row) => row.warnings_count ?? emptyValue,
      },
      {
        id: 'subscription',
        label: t('onboarding:readiness.columns.subscription'),
        render: (row) => (
          row.subscription_status
            ? (
              <StatusBadge
                status={row.subscription_status}
                category="subscription"
                size="Sm"
              />
            )
            : emptyValue
        ),
      },
      {
        id: 'database',
        label: t('onboarding:readiness.columns.database'),
        render: (row) => (
          row.database_status
            ? (
              <StatusBadge
                status={row.database_status}
                category="generic"
                apiLabel={row.database_status}
                size="Sm"
                tone="Neutral"
              />
            )
            : emptyValue
        ),
      },
      {
        id: 'actions',
        label: t('common:actions'),
        align: 'right',
        width: 72,
        render: (row) => (
          <IconButton
            size="Sm"
            tone="Neutral"
            aria-label={t('common:view_detail')}
            disabled={!row.tenant?.id}
            onClick={(event) => {
              event.stopPropagation();
              if (row.tenant?.id) {
                navigate(`/tenants/${row.tenant.id}`);
              }
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        ),
      },
    ],
    [emptyValue, navigate, t],
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('onboarding:readiness.title')}
        subtitle={t('onboarding:readiness.subtitle')}
      />

      <FilterBar type="Simple">
        <FilterSelect
          label={t('onboarding:readiness.overall_status')}
          hideLabel
          value={overallStatus}
          onChange={(event) => setOverallStatus(event.target.value)}
          minWidth={240}
        >
          {statusOptions.map((option) => (
            <FilterSelectOption key={option.value || 'all'} value={option.value}>
              {option.label}
            </FilterSelectOption>
          ))}
        </FilterSelect>
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('navigation:provisioning')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('onboarding:readiness.toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={rows}
            getRowKey={(row) => row.tenant?.id}
            emptyMessage={t('onboarding:readiness.no_tenants')}
            onRowClick={(row) => {
              if (row.tenant?.id) {
                navigate(`/tenants/${row.tenant.id}`);
              }
            }}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        )}
      </SectionCard>
    </Box>
  );
}
